import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../cn';

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('border-ink-200 shadow-soft rounded-xl border bg-white', className)}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'border-ink-200 flex items-start justify-between gap-4 border-b px-6 py-4',
        className,
      )}
      {...props}
    />
  );
}

export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3
      className={cn('font-display text-ink-900 text-base font-semibold tracking-tight', className)}
      {...props}
    />
  );
}

export function CardDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-ink-500 mt-1 text-sm', className)} {...props} />;
}

export function CardBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-6 py-5', className)} {...props} />;
}

export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'border-ink-200 bg-ink-50/50 flex items-center justify-end gap-2 border-t px-6 py-3',
        className,
      )}
      {...props}
    />
  );
}

/** Card específico para métricas — bem comum no admin/trader. */
export interface MetricCardProps {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  trend?: { value: string; positive?: boolean };
  className?: string;
}

export function MetricCard({ label, value, hint, trend, className }: MetricCardProps) {
  return (
    <Card className={cn('px-6 py-5', className)}>
      <p className="text-2xs text-ink-500 font-semibold uppercase tracking-wider">{label}</p>
      <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-ink-900 whitespace-nowrap font-mono text-2xl font-semibold tabular-nums">
          {value}
        </span>
        {trend && (
          <span
            className={cn(
              'text-xs font-medium',
              trend.positive ? 'text-success-500' : 'text-danger-500',
            )}
          >
            {trend.positive ? '↑' : '↓'} {trend.value}
          </span>
        )}
      </div>
      {hint && <p className="text-ink-500 mt-2 text-xs">{hint}</p>}
    </Card>
  );
}
